import {
  ArrowLeft,
  CheckCircle,
  Clock,
  Copy,
  QrCode,
  Smartphone,
} from 'lucide-react'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { appContext } from '../contexts/appContext'
import { usePaymentStatus } from '../hooks/usePaymentStatus'
import { AppContext } from '../types/app-context'
import { copyToClipboard } from '../utils/paymentApi'

const PixPaymentScreen: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState(120)
  const [isActive, setIsActive] = useState(true)
  const [copySuccess, setCopySuccess] = useState(false)
  const {
    isLoading,
    pixPaymentData,
    paymentValue,
    handlePixBack,
    handlePixSuccess,
    handleSubmit,
    setPixPaymentData,
    setPaymentValue,
    setIsLoading,
  } = useContext(appContext) as AppContext
  const { startPolling } = usePaymentStatus({
    onSucess: handlePixSuccess,
    setPixPaymentData,
    setPaymentValue,
  })
  const counterInterval = useRef<number | null>(null)

  // Separate effect for polling
  useEffect(() => {
    if (pixPaymentData?.id) {
      startPolling(pixPaymentData.id)
      setIsLoading(false)
    }
  }, [pixPaymentData?.id])

  // Timer countdown effect
  useEffect(() => {
    let interval: number | null = null
    interval = setInterval(() => {
      setTimeLeft((prevTime) => {
        const newTime = prevTime - 1
        if (newTime <= 0) {
          setIsActive(false)
          return 0
        }
        return newTime
      })
    }, 1000)
    counterInterval.current = interval

    return () => {
      if (counterInterval.current) {
        clearInterval(counterInterval.current)
      }
    }
  }, [])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    const formatted = `${String(mins).padStart(2, '0')}:${String(secs).padStart(
      2,
      '0'
    )}`
    return formatted
  }

  const handleCopy = async () => {
    if (pixPaymentData?.pixCode) {
      const success = await copyToClipboard(pixPaymentData.pixCode)
      if (success) {
        setCopySuccess(true)
        setTimeout(() => setCopySuccess(false), 2000)
      } else {
        toast.error('Erro ao copiar o código. Por favor, tente novamente.')
      }
    }
  }

  const getTimerColor = () => {
    if (timeLeft <= 30) return '#dc3545'
    if (timeLeft <= 60) return '#ffc107'
    return '#2B8AC8'
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b py-4">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handlePixBack}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors px-3 py-2 rounded hover:bg-gray-100"
              >
                <ArrowLeft size={20} />
                <span>Voltar</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-xl font-semibold text-gray-800">
                Pagamento PIX
              </h1>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">
                R$ {paymentValue.toFixed(2).replace('.', ',')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg border p-8">
          {/* Timer */}
          {isActive && (
            <div className="mb-8 p-4 bg-gray-50 rounded-lg text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Clock size={20} style={{ color: getTimerColor() }} />
                <span className="text-gray-600 font-medium">
                  Tempo restante para pagamento
                </span>
              </div>
              <div
                className="text-4xl font-bold"
                style={{ color: getTimerColor() }}
              >
                {formatTime(timeLeft)}
              </div>
            </div>
          )}

          {/* Expired Message */}
          {!isActive && timeLeft === 0 && (
            <div className="mb-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg text-center flex flex-col items-center">
              <p className="text-yellow-800 font-medium text-lg mb-4">
                Tempo para pagamento expirado
              </p>
              <button
                onClick={async (e) => {
                  setIsLoading((prev) => !prev)
                  await handleSubmit(e)
                  setTimeLeft(120)
                  setIsActive(true)
                }}
                className="px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium flex gap-2 w-fit disabled:pointer-events-none disabled:bg-opacity-75"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <i className="fas fa-spinner animate-spin h-6 w-6 text-[1.5rem]"></i>
                    <p>Processando pagamento...</p>
                  </>
                ) : (
                  'Gerar Novo PIX'
                )}
              </button>
            </div>
          )}

          {/* Payment Content */}
          {pixPaymentData && pixPaymentData.qrCode && isActive && (
            <div className="grid md:grid-cols-2 gap-8">
              {/* QR Code Section */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <QrCode size={24} className="text-blue-600" />
                  <h3 className="text-xl font-semibold text-gray-800">
                    Escaneie o QR Code
                  </h3>
                </div>
                <div className="bg-white p-6 rounded-lg border-2 border-blue-200 inline-block mb-4">
                  <img
                    src={pixPaymentData && pixPaymentData.qrCode}
                    alt="QR Code PIX"
                    className="w-48 h-48 object-contain"
                    onError={(e) => {
                      console.error('Erro ao carregar QR Code:', e)
                    }}
                  />
                </div>
                <p className="text-gray-600">
                  Use o aplicativo do seu banco para escanear
                </p>
              </div>

              {/* Instructions and PIX Code */}
              <div className="space-y-6">
                {/* PIX Code */}
                {pixPaymentData && pixPaymentData.pixCode && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Copy size={20} className="text-blue-600" />
                      <h4 className="text-lg font-semibold text-gray-800">
                        Código PIX
                      </h4>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg border mb-3">
                      <code className="text-sm break-all text-gray-700 font-mono leading-relaxed block">
                        {pixPaymentData && pixPaymentData.pixCode}
                      </code>
                    </div>
                    <button
                      onClick={handleCopy}
                      className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                        copySuccess
                          ? 'bg-green-600 text-white'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {copySuccess ? (
                        <>
                          <CheckCircle size={18} />
                          Código Copiado!
                        </>
                      ) : (
                        <>
                          <Copy size={18} />
                          Copiar Código PIX
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Instructions */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Smartphone size={20} className="text-blue-600" />
                    <h4 className="text-lg font-semibold text-gray-800">
                      Como pagar
                    </h4>
                  </div>
                  <ol className="text-gray-700 space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                        1
                      </span>
                      <span>Abra o aplicativo do seu banco</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                        2
                      </span>
                      <span>Procure pela opção PIX</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                        3
                      </span>
                      <span>Escaneie o QR Code ou cole o código PIX</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                        4
                      </span>
                      <span>Confirme o pagamento no seu banco</span>
                    </li>
                  </ol>
                </div>

                {/* Security Info */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-700 mb-2">
                    <i className="fas fa-shield-alt"></i>
                    <span className="font-medium">Pagamento 100% Seguro</span>
                  </div>
                  <p className="text-sm text-green-600">
                    Seus dados estão protegidos e criptografados
                  </p>
                </div>

                {/* Expiration */}
                {pixPaymentData && pixPaymentData.expirationDate && (
                  <div className="text-sm text-gray-500 text-center">
                    Válido até:{' '}
                    {new Date(
                      pixPaymentData && pixPaymentData.expirationDate
                    ).toLocaleString('pt-BR')}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PixPaymentScreen
